import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUserPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaChevronDown,
} from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import AccessDenied from "./AccessDenied";
import CreateUserModal from "./CreateUserModal";
import UpdateUser from "./UpdateUser"; // Import the UpdateUser component

const Users = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [roles] = useState(["staff", "admin"]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const { authState } = useContext(AuthContext);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/all`,
          {
            headers: {
              "x-auth-token": authState.token,
            },
          }
        );
        setUsers(response.data);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "An unexpected error occurred ⛔⛔"
        );
      }
    };

    fetchUsers();
  }, [authState.token]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${id}`,
        {
          headers: {
            "x-auth-token": authState.token,
          },
        }
      );
      setUsers(users.filter((user) => user._id !== id));
      toast.success("User deleted successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred ⛔⛔"
      );
    }
    setDeleteConfirm(null);
  };

  const handleRoleAssign = async (userId, newRole) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/assign-role`,
        { role: newRole },
        {
          headers: {
            "x-auth-token": authState.token,
          },
        }
      );

      const updatedUsers = users.map((user) =>
        user._id === userId ? { ...user, role: response.data.role } : user
      );
      setUsers(updatedUsers);
      toast.success("Role updated successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "An unexpected error occurred ⛔⛔"
      );
    }
  };

  const handleUserCreated = (newUser) => {
    setUsers([...users, newUser]);
  };

  const handleUserUpdated = (updatedUser) => {
    setUsers(
      users.map((user) => (user._id === updatedUser._id ? updatedUser : user))
    );
    setIsUpdateModalOpen(false);
    setEditingUserId(null);
  };

  const openUpdateModal = (userId) => {
    setEditingUserId(userId);
    setIsUpdateModalOpen(true);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const isAdmin = authState.user && authState.user.role === "admin";

  if (!isAdmin) {
    return (
      <div className="flex justify-center items-center h-screen">
        <AccessDenied />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center mt-16 p-4 min-h-screen bg-gray-100"
    >
      <div className="w-full max-w-screen-xl bg-white rounded-lg p-4 md:p-6 shadow-lg">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCreateModalOpen(true)}
            className="w-full md:w-auto flex items-center justify-center bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition duration-300"
          >
            <FaUserPlus className="mr-2" />
            Add User
          </motion.button>
          <div className="relative w-full md:w-64">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-4 px-6">Name</th>
                <th className="py-4 px-6 hidden md:table-cell">Email</th>
                <th className="py-4 px-6">Role</th>
                <th className="py-4 px-6">Action</th>
              </tr>
            </thead>
            <AnimatePresence>
              <tbody>
                {currentUsers.map((user) => (
                  <motion.tr
                    key={user._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border-b"
                  >
                    <td className="py-4 px-6">{`${user.firstname} ${user.lastname}`}</td>
                    <td className="py-4 px-6 hidden md:table-cell">
                      {user.email}
                    </td>
                    <td className="py-4 px-6">
                      <div className="relative">
                        <select
                          value={user.role}
                          onChange={(e) =>
                            handleRoleAssign(user._id, e.target.value)
                          }
                          className="appearance-none w-full bg-gray-100 border border-gray-300 rounded-md px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        >
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <FaChevronDown className="fill-current h-4 w-4" />
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-col sm:flex-row items-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => openUpdateModal(user._id)}
                          className="w-full sm:w-auto flex items-center justify-center bg-green-500 text-white px-3 py-1 rounded-full hover:bg-green-600 transition duration-300"
                        >
                          <FaEdit className="mr-1" />
                          Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setDeleteConfirm(user._id)}
                          className="w-full sm:w-auto flex items-center justify-center bg-red-500 text-white px-3 py-1 rounded-full hover:bg-red-600 transition duration-300"
                        >
                          <FaTrash className="mr-1" />
                          Delete
                        </motion.button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </AnimatePresence>
          </table>
        </div>
        <div className="mt-6 flex justify-center flex-wrap">
          {Array.from(
            { length: Math.ceil(filteredUsers.length / usersPerPage) },
            (_, index) => (
              <motion.button
                key={index + 1}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => paginate(index + 1)}
                className={`px-3 py-1 m-1 rounded-full ${
                  currentPage === index + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {index + 1}
              </motion.button>
            )
          )}
        </div>
      </div>
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onUserCreated={handleUserCreated}
      />
      {isUpdateModalOpen && (
        <UpdateUser
          id={editingUserId}
          onClose={() => setIsUpdateModalOpen(false)}
          onUserUpdated={handleUserUpdated}
        />
      )}
      {deleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        >
          <div className="bg-white p-6 rounded-lg max-w-sm w-full">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p>Are you sure you want to delete this user?</p>
            <div className="mt-4 flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setDeleteConfirm(null)}
                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-full mr-2"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDelete(deleteConfirm)}
                className="bg-red-500 text-white px-4 py-2 rounded-full"
              >
                Delete
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Users;
